import { Heart, Target, Users, MapPin } from 'lucide-react';
import { Link } from 'wouter';

import { PublicShell } from '@/components/public-shell';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export default function About() {
  return (
    <PublicShell routeKey="about">
      <section className="hero-gradient overflow-hidden border-b border-border/60">
        <div className="page-shell py-14">
          <p className="editorial-kicker">About Kalovate</p>
          <h1 className="section-title mt-4 max-w-3xl">
            Every community has people worth learning from.
          </h1>
          <p className="section-copy mt-5 max-w-2xl">
            Kalovate gives them a platform — and gives learners a way to find them. We started in
            the Hoosier Hills — South Central Indiana — and we're building outward from there.
          </p>
        </div>
      </section>

      <section className="page-section">
        <div className="page-shell grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Target className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-3xl font-semibold text-foreground">Why Kalovate?</h2>
            </div>
            <p className="text-base leading-7 text-muted-foreground mb-5">
              The name comes from two words: <em>Kalos</em> (Greek for beautiful, good, noble) and{' '}
              <em>elevate</em>. Together they point at the thing we're trying to do — lift people
              through skill, craft, and community.
            </p>
            <p className="text-base leading-7 text-muted-foreground">
              We built Kalovate because local talent is everywhere and opportunity is uneven. The
              ceramist teaching out of her studio, the chef who does private supper clubs, the
              woodworker who finally wants to share what he knows — these people deserve more than a
              Eventbrite link. And learners deserve more than a YouTube algorithm.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-5">
            <Card className="rounded-[1.75rem] border-border/70 text-center p-5">
              <CardContent className="pt-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <p className="font-semibold text-foreground">Rooted locally</p>
                <p className="text-sm text-muted-foreground mt-1 leading-5">
                  Starting in Hoosier Hills, growing with communities
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-[1.75rem] border-border/70 text-center p-5">
              <CardContent className="pt-4">
                <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-accent" />
                </div>
                <p className="font-semibold text-foreground">Creator-first</p>
                <p className="text-sm text-muted-foreground mt-1 leading-5">
                  90% revenue share, no platform overhead to navigate
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-[1.75rem] border-border/70 text-center p-5 col-span-2">
              <CardContent className="pt-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-6 h-6 text-green-600" />
                </div>
                <p className="font-semibold text-foreground">Built for people, not algorithms</p>
                <p className="text-sm text-muted-foreground mt-1 leading-5">
                  Small-group learning, real instructors, real places
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="page-section border-t border-border/60 bg-muted/40">
        <div className="page-shell-narrow">
          <h2 className="text-3xl font-semibold text-foreground mb-8">The founders</h2>

          <Card className="rounded-[1.75rem] border-border/70 p-8">
            <CardContent className="pt-0 space-y-5">
              <p className="text-base leading-7 text-muted-foreground">
                Kalovate was founded by Matt Michie and Mason Ferachi — two people who kept running
                into the same frustration: incredible local talent with nowhere good to teach, and
                curious locals with no easy way to find them.
              </p>

              <p className="text-base leading-7 text-muted-foreground">
                The idea took shape at Fig Tree Market in Ellettsville — a venue already hosting the
                kind of in-person, hands-on community experiences that most platforms completely
                ignore. We wanted to give those experiences a home online without stripping away
                what makes them work: the instructor who actually shows up, the small group that
                learns together, the conversation at the end of class.
              </p>

              <p className="text-base leading-7 text-muted-foreground">
                Kalovate is still early. We're building with creators and learners across Hoosier
                Hills first, then expanding to other communities ready for something better than
                mass-market online courses.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="page-section">
        <div className="page-shell">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-foreground">What we stand for</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="rounded-[1.75rem] border-border/70">
              <CardHeader>
                <CardTitle className="text-xl">Creators keep what they earn</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-6 text-muted-foreground">
                  We charge a small platform fee and pass the rest to the instructor. You shouldn't
                  need a 10,000-subscriber following to make teaching worth your time.
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-[1.75rem] border-border/70">
              <CardHeader>
                <CardTitle className="text-xl">Small groups, real learning</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-6 text-muted-foreground">
                  Kalovate is built around in-person workshops, intimate online sessions, and
                  experiences where the instructor actually knows the students' names.
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-[1.75rem] border-border/70">
              <CardHeader>
                <CardTitle className="text-xl">Rooted in real places</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-6 text-muted-foreground">
                  We organize everything around local communities so students discover what's nearby
                  and creators build a local reputation — not just a follower count.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="page-section border-t border-border/60 bg-muted/40">
        <div className="page-shell-narrow text-center">
          <h2 className="text-3xl font-semibold text-foreground mb-5">Get involved</h2>
          <p className="section-copy mb-8">
            Whether you want to learn something new, share your expertise, or bring Kalovate to your
            community — we'd love to hear from you.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/regions/monroe-county/bloomington/classes">
              <Button
                size="lg"
                className="touch-target rounded-full px-8 font-semibold"
                data-testid="button-browse-classes"
              >
                Browse Classes
              </Button>
            </Link>

            <Link href="/creator/apply">
              <Button
                variant="outline"
                size="lg"
                className="touch-target rounded-full px-8 font-semibold"
                data-testid="button-become-creator"
              >
                Teach on Kalovate
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
